/**
 * Firebase → Postgres pull (panel-side), mirror of firebasePushService.
 * Reads events-v2 + stocks + tickets + sales + eventLayouts (+ layout) then upserts via dataProvider.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../database/firebase';
import { analyzeSeatConflicts, getTicketSeats } from '../utils/seatConflictUtils';

const serializeValue = (value) => {
  if (value == null) return value;
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().toISOString();
    } catch {
      return value;
    }
  }
  if (typeof value === 'object' && value.seconds != null && value.nanoseconds != null) {
    return new Date(value.seconds * 1000).toISOString();
  }
  if (Array.isArray(value)) return value.map(serializeValue);
  if (typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = serializeValue(v);
    }
    return out;
  }
  return value;
};

async function getDocById(collectionName, id) {
  if (!id) return null;
  const snap = await getDoc(doc(db, collectionName, id));
  if (!snap.exists()) return null;
  return serializeValue({ id: snap.id, ...snap.data() });
}

async function queryByEventField(collectionName, fieldPath, eventId) {
  const q = query(collection(db, collectionName), where(fieldPath, '==', eventId));
  const snap = await getDocs(q);
  const rows = [];
  snap.forEach((d) => rows.push(serializeValue({ id: d.id, ...d.data() })));
  return rows;
}

async function upsertResource(dataProvider, resource, id, data) {
  // Panel collection PUT upserts when missing (eventLayouts, seatSelections, tickets, …)
  await dataProvider.update(resource, { id, data: { ...data, id } });
  return { resource, id, action: 'upsert' };
}

async function upsertResourceSafe(dataProvider, resource, id, data, errors) {
  try {
    return await upsertResource(dataProvider, resource, id, data);
  } catch (err) {
    const message = err?.message || String(err);
    console.error(`Upsert failed ${resource}/${id}:`, message);
    errors.push({ resource, id, message });
    return null;
  }
}

async function deleteResourceSafe(dataProvider, resource, id, errors) {
  try {
    await dataProvider.delete(resource, { id });
    return { resource, id, action: 'delete' };
  } catch (err) {
    const message = err?.message || String(err);
    console.error(`Delete failed ${resource}/${id}:`, message);
    errors.push({ resource, id, message });
    return null;
  }
}

async function listAllByEvent(dataProvider, resource, filterKey, eventId) {
  const all = [];
  let page = 1;
  while (true) {
    const { data, total } = await dataProvider.getList(resource, {
      filter: { [filterKey]: eventId },
      pagination: { page, perPage: 1000 },
      sort: { field: 'id', order: 'ASC' },
    });
    all.push(...(data || []));
    if (!data?.length || all.length >= (total || 0) || data.length < 1000) break;
    page += 1;
  }
  return all;
}

/** Seat map: meta alanlari at, sadece koltuk entry'leri */
function packSeatSelectionsBody(sel) {
  if (!sel || typeof sel !== 'object') return {};
  const skip = new Set([
    'id',
    'createdate',
    'lastupdate',
    'createdby',
    'updatedby',
    'body',
  ]);
  const body = {};
  Object.entries(sel).forEach(([k, v]) => {
    if (skip.has(k)) return;
    if (v && typeof v === 'object' && !Array.isArray(v)) body[k] = v;
  });
  return body;
}

/** Postgres'te panelin isaretledigi iade bilet */
function isPostgresRefundedTicket(ticket) {
  return Boolean(ticket?.extras?.refunded === true || ticket?.refunded === true);
}

/** Postgres'te iade satis (API status 3) */
function isPostgresRefundedSale(sale) {
  return Number(sale?.status) === 3 || sale?.extras?.refunded === true;
}

function collectSeatsFromSaleOrTicket(row) {
  const ids = new Set();
  const push = (arr) => {
    (arr || []).forEach((s) => {
      if (s == null) return;
      if (typeof s === 'string') ids.add(s);
      else if (s.id != null) ids.add(String(s.id));
    });
  };
  push(row?.seats);
  push(row?.extras?.seats);
  push(row?.details?.seats);
  getTicketSeats(row).forEach((s) => {
    if (s?.id != null) ids.add(String(s.id));
  });
  (row?.tickets || []).forEach((t) => {
    push(t?.seats);
    push(t?.extras?.seats);
  });
  return ids;
}

/** Postgres iade kayitlarinin koltuklarini FB kilidinden bosalt (admin rezerv haric) */
function unlockSeatsInMap(selMap, seatIds, keepSeatIds) {
  const now = String(Math.floor(Date.now() / 1000));
  let unlocked = 0;
  seatIds.forEach((seatId) => {
    if (keepSeatIds.has(seatId)) return;
    const prev = selMap[seatId];
    if (prev?.isBlockedSeat === true) return;
    selMap[seatId] = {
      status: 0,
      user: null,
      statusChangedAt: now,
      isBlockedSeat: false,
    };
    unlocked += 1;
  });
  return unlocked;
}

function packTicketForApi(raw) {
  const known = [
    'id',
    'pnr',
    'price',
    'channel',
    'isUsed',
    'sale',
    'venue',
    'isUsedBy',
    'isUsedAt',
    'product',
    'event',
    'user',
    'created',
    'extras',
  ];
  const knownSet = new Set(known);
  const extras = {
    ...(raw.extras && typeof raw.extras === 'object' ? raw.extras : {}),
  };
  Object.entries(raw).forEach(([k, v]) => {
    if (!knownSet.has(k) && k !== 'createdby' && k !== 'updatedby' && k !== 'lastupdate' && k !== 'createdate') {
      extras[k] = v;
    }
  });
  // Ensure seats land in extras (Prisma TicketRecord has no seats column)
  if (raw.seats != null && extras.seats == null) extras.seats = raw.seats;

  return {
    id: raw.id,
    pnr: raw.pnr ?? null,
    price: typeof raw.price === 'number' ? raw.price : null,
    channel: raw.channel ?? null,
    isUsed: raw.isUsed === true,
    sale: raw.sale ?? null,
    venue: raw.venue ?? null,
    isUsedBy: raw.isUsedBy ?? null,
    isUsedAt: raw.isUsedAt != null ? String(raw.isUsedAt) : null,
    product: raw.product ?? null,
    event: raw.event ?? null,
    user: raw.user ?? null,
    created: typeof raw.created === 'number' ? raw.created : Date.now(),
    extras: Object.keys(extras).length ? extras : null,
  };
}

function packSaleForApi(raw) {
  const known = new Set([
    'id',
    'status',
    'channel',
    'paymentType',
    'paymentTotal',
    'createdate',
    'oid',
    'user',
    'payment',
    'event',
    'details',
    'tickets',
    'extras',
  ]);
  const extras = {
    ...(raw.extras && typeof raw.extras === 'object' ? raw.extras : {}),
  };
  Object.entries(raw).forEach(([k, v]) => {
    if (!known.has(k) && !['createdby', 'updatedby', 'lastupdate'].includes(k)) {
      extras[k] = v;
    }
  });
  return {
    id: raw.id,
    status: raw.status ?? null,
    channel: raw.channel ?? null,
    paymentType: raw.paymentType ?? null,
    paymentTotal: raw.paymentTotal ?? null,
    createdate: raw.createdate ?? Date.now(),
    oid: raw.oid ?? null,
    user: raw.user ?? null,
    payment: raw.payment ?? null,
    event: raw.event ?? null,
    details: raw.details ?? null,
    tickets: raw.tickets ?? null,
    extras: Object.keys(extras).length ? extras : null,
  };
}

function packStockForApi(raw, eventId) {
  return {
    id: raw.id,
    stockID: raw.stockID || raw.id,
    event: raw.event || eventId,
    date: raw.date ?? null,
    time: raw.time ?? null,
    type: raw.type ?? 3,
    stock: raw.stock ?? null,
    sales: raw.sales ?? [],
    isActive: raw.isActive !== false,
    updatedate: raw.updatedate ?? null,
  };
}

/**
 * Pull Firebase → Postgres REPLACE.
 * Firebase'de iade yok. Postgres'te panelin iade isaretledigi ticket/sale
 * SILINMEZ ve FB ile uzerine yazilmaz. Firebase push yapilmaz.
 */
export async function pullEventFromFirebase(eventId, dataProvider) {
  if (!eventId) throw new Error('Event id yok');
  if (!dataProvider) throw new Error('dataProvider gerekli');

  const event = await getDocById('events-v2', eventId);
  if (!event) {
    throw new Error(
      `Firebase events-v2/${eventId} bulunamadı. Önce event Firebase'e push edilmiş olmalı.`,
    );
  }

  const results = [];
  const errors = [];
  const stocks = await queryByEventField('stocks', 'event', eventId);
  const tickets = await queryByEventField('tickets', 'event.id', eventId);
  const sales = await queryByEventField('sales', 'event.id', eventId);
  let salesV2 = [];
  try {
    salesV2 = await queryByEventField('sales-v2', 'event.id', eventId);
  } catch (e) {
    console.warn('sales-v2 pull atlandı:', e?.message || e);
  }

  let eventLayout = null;
  if (event.layoutJSONID) {
    eventLayout = await getDocById('eventLayouts', event.layoutJSONID);
  }
  if (!eventLayout && event.layoutID) {
    eventLayout = await getDocById('eventLayouts', event.layoutID);
  }
  if (!eventLayout) {
    const layouts = await queryByEventField('eventLayouts', 'event', eventId);
    eventLayout = layouts[0] || null;
  }

  let baseLayout = null;
  if (event.layoutID) {
    baseLayout = await getDocById('layouts', event.layoutID);
  }

  let pgTicketsBefore = [];
  let pgSalesBefore = [];
  let pgSalesV2Before = [];
  try {
    pgTicketsBefore = await listAllByEvent(dataProvider, 'tickets', 'event.id', eventId);
  } catch (e) {
    console.warn('pg tickets list atlandı:', e?.message || e);
  }
  try {
    pgSalesBefore = await listAllByEvent(dataProvider, 'sales', 'event.id', eventId);
  } catch (e) {
    console.warn('pg sales list atlandı:', e?.message || e);
  }
  try {
    pgSalesV2Before = await listAllByEvent(dataProvider, 'sales-v2', 'event.id', eventId);
  } catch (e) {
    console.warn('pg sales-v2 list atlandı:', e?.message || e);
  }

  const preservedTicketIds = new Set(
    pgTicketsBefore.filter(isPostgresRefundedTicket).map((t) => String(t.id)),
  );
  const preservedSaleIds = new Set(
    pgSalesBefore.filter(isPostgresRefundedSale).map((s) => String(s.id)),
  );
  const preservedSaleV2Ids = new Set(
    pgSalesV2Before.filter(isPostgresRefundedSale).map((s) => String(s.id)),
  );

  const preservedRefundSeats = new Set();
  pgTicketsBefore
    .filter(isPostgresRefundedTicket)
    .forEach((t) => collectSeatsFromSaleOrTicket(t).forEach((id) => preservedRefundSeats.add(id)));
  [...pgSalesBefore, ...pgSalesV2Before]
    .filter(isPostgresRefundedSale)
    .forEach((s) => collectSeatsFromSaleOrTicket(s).forEach((id) => preservedRefundSeats.add(id)));

  const seatSelectionDocs = [];
  for (const stock of stocks) {
    const col = await getDocById('seatSelections', stock.id);
    const embedded =
      stock.seatSelections && typeof stock.seatSelections === 'object'
        ? packSeatSelectionsBody({ id: stock.id, ...stock.seatSelections })
        : {};
    const fromCol = col ? packSeatSelectionsBody(col) : {};
    // Col ustune yazar; ama canli web (stocks.seatSelections) admin rezervlerini
    // ezmesin — bilet/satis yoksa isBlockedSeat stocks'tan korunur.
    const merged = { ...embedded, ...fromCol };
    Object.entries(embedded).forEach(([seatId, stockEntry]) => {
      if (!stockEntry || stockEntry.isBlockedSeat !== true) return;
      const colEntry = fromCol[seatId];
      const colSold = colEntry && Number(colEntry.status) === 1 && colEntry.isBlockedSeat !== true;
      // Gercek satis gibi gorunen col kaydini bozma; bos/hold/blocked ise stocks admin rezerv gelsin
      if (colSold) return;
      merged[seatId] = {
        ...(colEntry && typeof colEntry === 'object' ? colEntry : {}),
        ...stockEntry,
        status: 1,
        isBlockedSeat: true,
      };
    });
    if (Object.keys(merged).length) {
      seatSelectionDocs.push({ id: stock.id, ...merged });
    }
  }

  const fbActiveSeatIds = new Set();
  tickets.forEach((t) => {
    if (preservedTicketIds.has(String(t.id))) return;
    collectSeatsFromSaleOrTicket(t).forEach((id) => fbActiveSeatIds.add(id));
  });
  sales
    .filter((s) => Number(s.status) === 1 && !preservedSaleIds.has(String(s.id)))
    .forEach((s) => {
      collectSeatsFromSaleOrTicket(s).forEach((id) => fbActiveSeatIds.add(id));
    });

  let unlockedSeats = 0;
  for (const selDoc of seatSelectionDocs) {
    unlockedSeats += unlockSeatsInMap(selDoc, preservedRefundSeats, fbActiveSeatIds);
  }

  if (baseLayout?.layout) {
    const r = await upsertResourceSafe(
      dataProvider,
      'layouts',
      baseLayout.id,
      {
        name: baseLayout.name || null,
        venue: baseLayout.venue || null,
        layoutType: baseLayout.layoutType || 'seating',
        layout: baseLayout.layout,
      },
      errors,
    );
    if (r) results.push(r);
  }

  if (eventLayout) {
    const r = await upsertResourceSafe(
      dataProvider,
      'eventLayouts',
      eventLayout.id,
      {
        event: eventLayout.event || eventId,
        layoutJSON: eventLayout.layoutJSON ?? null,
      },
      errors,
    );
    if (r) results.push(r);
  }

  for (const stock of stocks) {
    const r = await upsertResourceSafe(
      dataProvider,
      'stocks',
      stock.id,
      packStockForApi(stock, eventId),
      errors,
    );
    if (r) results.push(r);
  }

  const stockIdsWithSel = new Set(seatSelectionDocs.map((s) => s.id));
  for (const stock of stocks) {
    if (stockIdsWithSel.has(stock.id)) continue;
    try {
      await dataProvider.getOne('seatSelections', { id: stock.id });
      const del = await deleteResourceSafe(dataProvider, 'seatSelections', stock.id, errors);
      if (del) results.push(del);
    } catch {
      // yok
    }
  }

  for (const sel of seatSelectionDocs) {
    const body = packSeatSelectionsBody(sel);
    try {
      await dataProvider.getOne('seatSelections', { id: sel.id });
      await deleteResourceSafe(dataProvider, 'seatSelections', sel.id, errors);
    } catch {
      // yok
    }
    const r = await upsertResourceSafe(
      dataProvider,
      'seatSelections',
      sel.id,
      { ...body, _replaceBody: true },
      errors,
    );
    if (r) results.push({ ...r, action: 'replace' });
  }

  let deletedTickets = 0;
  let deletedSales = 0;
  let deletedSalesV2 = 0;
  const preservedTickets = preservedTicketIds.size;
  const preservedSales = preservedSaleIds.size + preservedSaleV2Ids.size;

  for (const t of pgTicketsBefore) {
    if (!t?.id) continue;
    if (preservedTicketIds.has(String(t.id))) continue;
    const del = await deleteResourceSafe(dataProvider, 'tickets', t.id, errors);
    if (del) {
      results.push(del);
      deletedTickets += 1;
    }
  }

  for (const ticket of tickets) {
    if (preservedTicketIds.has(String(ticket.id))) continue;
    const r = await upsertResourceSafe(
      dataProvider,
      'tickets',
      ticket.id,
      packTicketForApi(ticket),
      errors,
    );
    if (r) results.push(r);
  }

  for (const s of pgSalesBefore) {
    if (!s?.id) continue;
    if (preservedSaleIds.has(String(s.id))) continue;
    const del = await deleteResourceSafe(dataProvider, 'sales', s.id, errors);
    if (del) {
      results.push(del);
      deletedSales += 1;
    }
  }

  for (const sale of sales) {
    if (preservedSaleIds.has(String(sale.id))) continue;
    const r = await upsertResourceSafe(
      dataProvider,
      'sales',
      sale.id,
      packSaleForApi(sale),
      errors,
    );
    if (r) results.push(r);
  }

  for (const s of pgSalesV2Before) {
    if (!s?.id) continue;
    if (preservedSaleV2Ids.has(String(s.id))) continue;
    const del = await deleteResourceSafe(dataProvider, 'sales-v2', s.id, errors);
    if (del) {
      results.push(del);
      deletedSalesV2 += 1;
    }
  }

  for (const sale of salesV2) {
    if (preservedSaleV2Ids.has(String(sale.id))) continue;
    const r = await upsertResourceSafe(
      dataProvider,
      'sales-v2',
      sale.id,
      packSaleForApi(sale),
      errors,
    );
    if (r) results.push(r);
  }

  const eventPayload = {
    name: event.name,
    description: event.description ?? null,
    details: event.details ?? null,
    category: event.category ?? null,
    subcategories: event.subcategories ?? null,
    type: event.type ?? 1,
    city: event.city ?? null,
    venue: event.venue ?? null,
    startdate: event.startdate ?? null,
    enddate: event.enddate ?? null,
    duration: event.duration ?? 0,
    dateType: event.dateType || 'oneoff',
    sessions: event.sessions ?? null,
    paymentMethods: event.paymentMethods ?? null,
    coupons: event.coupons ?? null,
    rules: event.rules ?? null,
    badge: event.badge ?? null,
    banner: event.banner ?? null,
    venueLayout: event.venueLayout ?? null,
    videoURL: event.videoURL ?? null,
    commissionFee: event.commissionFee ?? 0,
    isCommissionExtra: event.isCommissionExtra === true,
    servicefee: event.servicefee ?? 0,
    organisationCompanies: event.organisationCompanies ?? null,
    layoutID: event.layoutID ?? null,
    layoutJSONID: event.layoutJSONID ?? null,
    isActive: event.isActive !== false,
    isVerified: event.isVerified === true,
    isCancelled: event.isCancelled === true,
    notify: event.notify !== false,
    refundEnabled: event.refundEnabled !== false,
    refundDeadlineHours: event.refundDeadlineHours ?? null,
    refundDeadlineAt: event.refundDeadlineAt ?? null,
  };
  {
    const r = await upsertResourceSafe(
      dataProvider,
      'events-v2',
      eventId,
      eventPayload,
      errors,
    );
    if (r) results.push(r);
  }

  const primarySeatSelections = seatSelectionDocs[0] || null;
  let layoutJSON = eventLayout?.layoutJSON ?? null;
  if (typeof layoutJSON === 'string') {
    try {
      layoutJSON = JSON.parse(layoutJSON);
    } catch {
      layoutJSON = null;
    }
  }

  const ticketsForAnalysis = tickets
    .filter((t) => !preservedTicketIds.has(String(t.id)))
    .map((t) => ({
      ...t,
      seats: getTicketSeats(t).length ? getTicketSeats(t) : t.seats,
    }));

  const analysis = analyzeSeatConflicts({
    tickets: ticketsForAnalysis,
    layoutJSON,
    seatSelections: primarySeatSelections,
  });

  return {
    eventId,
    eventName: event.name,
    pulledAt: new Date().toISOString(),
    mode: 'replace-preserve-pg-refunds',
    counts: {
      stocks: stocks.length,
      tickets: tickets.length,
      sales: sales.length,
      salesV2: salesV2.length,
      seatSelections: seatSelectionDocs.length,
      eventLayout: eventLayout ? 1 : 0,
      layout: baseLayout ? 1 : 0,
      deletedTickets,
      deletedSales,
      deletedSalesV2,
      preservedTickets,
      preservedSales,
      unlockedSeats,
      upserted: results.length,
      failed: errors.length,
    },
    results,
    errors,
    conflicts: analysis.conflicts,
    analysis,
  };
}

export default {
  pullEventFromFirebase,
};
